import { getDefaultInputValues, getRiskBandsMode } from "@/features/branding";
import { decryptData, decryptPathwayActions, decryptPortfolioAssets, getSecretKey } from "@/features/cryptography";
import { UTF8_BOM } from "@/features/custom_downloads/utils";
import { store } from "@/store";
import { facadesUrl } from "@/store/services/facades";
import { AdaptationPathwayAction, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import {
  PortfolioExportResultError,
  PortfolioExportSummaryRequest,
  PortfolioRegionType,
} from "@/tools/aggregate/portfolio-export/types";
import { AdaptationType } from "@/types/adaptation";
import { KeyPairAndSalt, SecretSalt } from "@/types/cryptography";
import { hexToArrayBuffer } from "@/utils/crypto";
import { getUTCDate } from "@/utils/datetime";
import { unparse } from "@climaterisk/papaparse";
import { RcFile } from "antd/es/upload";

/**
 *
 * @param file -
 * @returns
 */
export const buildUploadStream = (file: RcFile): ReadableStream => {
  const reader = file.stream().getReader();

  return new ReadableStream({
    pull: async (controller) => {
      const { done, value } = await reader.read();

      if (done) {
        controller.close();
        return;
      }

      controller.enqueue(value);
    },
  });
};

export const streamResults = async (
  ids: string[],
  stamps: string[],
  regionType?: PortfolioRegionType
): Promise<any> => {
  const url = new URL(`${facadesUrl}${getModeEndpoint(regionType)}/results`);
  if (stamps.length) {
    stamps.forEach((stamp) => url.searchParams.append("stamps", stamp));
  }

  // Send all IDs to Facades batch endpoint.
  // TODO: Move at least some of this code to the Facades API wrapper.
  return fetch(url.toString(), {
    method: "POST",
    headers: {
      Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(ids),
  }).then((data) => {
    return {
      type: "success",
      stream: data.body,
    };
  });
};

export const downloadPortfolioSummary = async (
  summaryId: string,
  settings?: { currency_code?: string; resilience_pathway?: AdaptationType }
) => {
  const res = await fetch(`${facadesUrl}portfolio/download`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
      Accept: "application/vnd.oasis.opendocument.spreadsheet",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ report_id: summaryId, settings }),
  });

  if (!res.ok) {
    throw new Error("Portfolio Summary not found");
  }

  return res.arrayBuffer();
};

export const getPortfolioUsageStream = (orgKey: KeyPairAndSalt, userKey: KeyPairAndSalt): ReadableStream => {
  let count = 0;
  const batchSize = 100;
  const timePeriod = { from: getUTCDate(2022, 1, 1), until: getUTCDate(new Date().getFullYear() + 1, 0, 1) };

  const encoder = new TextEncoder();

  return new ReadableStream({
    start: (controller) => {
      count = 0;

      controller.enqueue(UTF8_BOM);
      controller.enqueue(
        encoder.encode(
          [
            "Run Date",
            "User",
            "Group",
            "Analysis ID",
            "Analysis Name",
            "Requested",
            "Errored",
            "Analysed",
            "Date Deleted",
          ].join(",") + "\n"
        )
      );
    },
    pull: async (controller) => {
      const action = supabaseApi.endpoints.getAllPortfolios.initiate({
        dateFrom: timePeriod.from,
        dateUntil: timePeriod.until,
        page: count + 1,
        pageSize: batchSize,
      });

      const data = await dispatchWithRetries<any[]>({ action, maxRetries: 3, timeout: 5000 });
      count++;

      if (data === undefined || !data.length) {
        controller.close();
        return;
      }
      // decrypt portfolio results
      const portfolios = await Promise.all(
        data.map(async (item) => {
          try {
            if (!item.secret_enc) throw Error;

            const secret = item.is_private
              ? await getSecretKey(userKey.keyPair.privateKey, item.secret_enc)
              : await getSecretKey(orgKey.keyPair.privateKey, item.secret_enc);
            const name = await decryptData(secret, hexToArrayBuffer(item.name || ""));

            return {
              ...item,
              key: item.id,
              name,
            };
          } catch {
            return {
              ...item,
              key: item.id,
              name: "[Encrypted Name]",
            };
          }
        })
      );

      let rows = "";
      portfolios.map((portfolio) => {
        const row = [
          portfolio.created_at,
          portfolio.email,
          portfolio.group_name,
          portfolio.id,
          portfolio.name,
          portfolio.total ?? 0,
          portfolio.error ?? 0,
          portfolio.success ?? 0,
          portfolio.deleted_at,
        ];
        rows += unparse([row]) + "\n";
      });
      controller.enqueue(encoder.encode(rows));
    },
  });
};

const preparePayload = async (portfolioId: string, secret: SecretSalt, stamps: string[], regionType?: string) => {
  // Would be nice to consolidate this to IndexedDb in the future.
  const action = supabaseApi.endpoints.getAllPortfolioAssetsEncrypted.initiate(portfolioId);
  type ActionType = (typeof supabaseApi.endpoints.getAllPortfolioAssetsEncrypted)["Types"]["ResultType"];

  const encryptedAssets = await dispatchWithRetries<ActionType>({
    action,
    maxRetries: 10,
    timeout: 3000,
  });

  if (!encryptedAssets) {
    throw new Error("Failed to get assets for portfolio.");
  }

  let assets: {
    data: any;
    uuid: string;
  }[] = [];

  try {
    assets = await decryptPortfolioAssets(secret, encryptedAssets);
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : `${err}`);
  }

  const body: PortfolioExportSummaryRequest = {
    assets: assets.map(({ uuid, data }) => {
      const { marketValue, mortgageRate, replacementCost } = getDefaultInputValues(data?.archetype, regionType);
      return {
        uuid,
        mv: data.financial?.market_value ?? marketValue,
        rc: data.financial?.replacement_cost ?? replacementCost,
        mr: data.financial?.interest_rate ?? mortgageRate,
      };
    }),
  };

  if (stamps.length) {
    body.stamps = stamps;
  }

  return body;
};

const createPortfolioSummary = async (body: PortfolioExportSummaryRequest): Promise<string> => {
  console.log(`Creating portfolio summary`);

  try {
    const res = await fetch(`${facadesUrl}portfolio/request`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(`Request failed with status: ${res.status}`);
    }

    const responseData = await res.json();
    const summaryId = responseData.report_id;

    if (!summaryId) {
      throw new Error("No report_id returned from request");
    }

    return summaryId;
  } catch (err) {
    throw {
      type: "failure",
      errors: [err],
    };
  }
};

/**
 * Check the progress of a portfolio summary generation
 * @param summaryIds - Array of report IDs to check progress for
 * @returns Promise with progress data including status and queue position
 */
export const checkPortfolioProgress = async (
  summaryIds: string[]
): Promise<
  {
    report_id: string;
    status: string;
    position?: number;
  }[]
> => {
  const res = await fetch(`${facadesUrl}portfolio/progress`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ report_ids: summaryIds }),
  });

  if (!res.ok) {
    throw new Error(`Progress check failed with status: ${res.status}`);
  }

  return await res.json();
};

/**
 * Retrieve a summary of a Portfolio Export summary.
 * @param portfolioId - ID of the Portfolio Export portfolio we want a summary of.
 * @param previousSummaryId - ID of previously generated summary
 * @param secret - Current user's credentials for decrypting Supabase data.
 * @param stamps - Array of stamp identifiers
 */
export const enqueuePortfolioSummary = async (
  portfolioId: string,
  previousSummaryId: string,
  secret: SecretSalt,
  stamps: string[],
  regionType?: string
): Promise<void> => {
  let summaryId = previousSummaryId;

  if (!previousSummaryId.length) {
    const body = await preparePayload(portfolioId, secret, stamps, regionType);
    summaryId = await createPortfolioSummary(body);

    await store.dispatch(
      supabaseApi.endpoints.updatePortfolioReport.initiate({
        id: portfolioId,
        summaryId,
      })
    );
  }
};

export const enqueuePortfolioAdaptationSummary = async (
  portfolioId: string,
  previousSummaryId: string,
  secret: SecretSalt,
  stamps: string[],
  adaptationId: string,
  regionType?: string
): Promise<void> => {
  const action = supabaseApi.endpoints.getAllPortfolioAdaptationAnalysisIds.initiate({
    portfolioId: portfolioId,
    adaptationId: adaptationId,
  });

  let summaryId = previousSummaryId;
  if (!previousSummaryId?.length) {
    const encryptedActions = await dispatchWithRetries<any>({
      action,
      maxRetries: 10,
      timeout: 3000,
    });
    if (!encryptedActions) {
      throw new Error("Failed to get actions for portfolio adaptation.");
    }
    let decryptedActions: { data: any; uuid: string }[] = [];

    try {
      decryptedActions = await decryptPathwayActions(
        secret,
        encryptedActions.map((action: AdaptationPathwayAction) => ({
          encryptedId: action.encrypted_id,
          data: action.data,
        }))
      );
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : `${err}`);
    }

    const body: PortfolioExportSummaryRequest = {
      assets: decryptedActions.map(({ uuid, data }) => {
        const { marketValue, mortgageRate, replacementCost } = getDefaultInputValues(data?.archetype, regionType);

        return {
          uuid,
          mv: data.financial?.market_value ?? marketValue,
          rc: data.financial?.replacement_cost ?? replacementCost,
          mr: data.financial?.interest_rate ?? mortgageRate,
        };
      }),
    };
    if (stamps.length) {
      body.stamps = stamps;
    }

    summaryId = await createPortfolioSummary(body);

    await store.dispatch(
      supabaseApi.endpoints.updatePortfolioAdaptationReport.initiate({
        id: adaptationId,
        summaryId,
      })
    );
  }
};

export interface CompletedAssets {
  successes: string[];
  errors: PortfolioExportResultError[];
}

export const streamProgress = async (
  ids: string[],
  signal: AbortSignal | null = null,
  regionType?: PortfolioRegionType
): Promise<any> => {
  return fetch(`${facadesUrl}${getModeEndpoint(regionType)}/progress`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(ids),
    signal,
  }).then((data) => ({
    type: "success",
    stream: data.body,
  }));
};

export const streamResultsErrorsOnly = (ids: string[], regionType?: PortfolioRegionType): Promise<any> => {
  return new Promise((resolve, reject) => {
    fetch(`${facadesUrl}${getModeEndpoint(regionType)}/results?mode=only_api_errors`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(ids),
    })
      .then((data) => {
        resolve({
          type: "success",
          stream: data.body,
        });
      })
      .catch((err) => {
        reject({
          type: "failure",
          errors: [err],
        });
      });
  });
};

const getModeEndpoint = (regionType?: PortfolioRegionType) => {
  // Global needs comprehensive due to HKMA's keys not having simple.
  // We can probably just replace this wholesale, but this is a good
  // way to test it + have cover in case of issues (Global is new,
  // so bugs expected).
  const mode = regionType === "global" ? "comprehensive" : getRiskBandsMode();
  switch (mode) {
    case "abc":
      throw new Error("Portfolio Analysis not implemented for ABC.");

    case "halo":
      return "project_halo";

    case "simple":
      return "structural/simple";

    case "comprehensive":
      return "structural/comprehensive";
  }
};
